import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  DataStateChangeEvent,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import {
  ColumnOptions,
  DataGridComponent,
  GridRowType,
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  ValidationError,
  ValidationResultService,
} from '@shared';
import { AlertsService, GlobalEventsService } from '@shared-data';
import { RestaurantItemService } from '../../services';
import { GridRowOverride } from '../../models';

@Component({
  selector: 'app-override-grid',
  templateUrl: './override-grid.component.html',
  styleUrl: './override-grid.component.scss',
})
export class OverrideGridComponent implements OnInit {
  @Input()
  componentId!: number;

  @Input()
  onlyEntrees: boolean = false;

  @Input()
  restaurantId!: string;

  @Input()
  productType!: string;

  @Input()
  locked: boolean = false;

  state = {} as GridState;

  fontSize: number = 0.875;

  columns: ColumnOptions[] = [];

  overrides: GridDataResult = {
    data: [],
    total: 0,
  };

  componentName: string = '';
  componentType: string = '';
  componentDefaultPrice: number = 0;

  cache: Map<string, GridRowOverride> = new Map<string, GridRowOverride>();

  comboSearch: Subject<string> = new Subject<string>();

  @ViewChild('grid', { static: false })
  grid!: DataGridComponent;

  @Output()
  valueChange: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private service: RestaurantItemService,
    private loaderService: LoaderService,
    private messageService: MessageService,
    private globalEventsService: GlobalEventsService,
    private validationErrorService: ValidationResultService,
  ) {
    this.columns = new GridRowOverride().getColumns();
  }

  setDefaultState(): void {
    if (!this.restaurantId) {
      return;
    }

    this.state = new GridState(
      GridState.getPageSizeByFontSize(this.fontSize),
      [{ field: 'comboName', dir: 'asc' }],
      this.restaurantId,
      this.productType,
    );

    this.fetchOverrides(this.state);
  }

  fetchOverrides(event: GridState | DataStateChangeEvent) {
    const state = event as GridState;

    this.loaderService.show();
    if (state.filter) {
      this.state.filter = state.filter;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = [{ field: 'comboName', dir: 'asc' }];
    }

    if (state.skip !== undefined) this.state.skip = state.skip;

    if (state.take !== undefined) this.state.take = state.take;

    this.service
      .getOverrides(this.state, this.componentId, this.onlyEntrees)
      .subscribe({
        next: (gridDataResult: GridDataResult) => {
          let i = 0;
          gridDataResult.data = gridDataResult.data.map((row) => {
            row.id = row.id ?? --i - (this.state.skip ?? 0);
            const gridRow = new GridRowOverride(row);

            gridRow.locked =
              this.locked ||
              gridRow.newIsOverridePrice === false ||
              gridRow.newIsOverridePrice == null;
            return gridRow;
          });

          this.componentName = gridDataResult.data[0]?.componentName || '';
          this.componentType = gridDataResult.data[0]?.componentType || '';
          this.componentDefaultPrice =
            gridDataResult.data[0]?.componentPrice || 0;

          this.overrides = gridDataResult;

          if (this.onlyEntrees) this.markSecondaryComponents();

          this.restoreCachedChanges();

          this.loaderService.hide();
        },
        error: (error: any) => {
          this.messageService.error('Error fetching upcharge overrides');
          var validationError = error?.error;
          if (validationError)
            this.validationErrorService.show(validationError);
          this.loaderService.hide();
        },
      });
  }

  ngOnInit() {
    this.subscribeToAutocomplete();

    this.setDefaultState();
  }

  subscribeToAutocomplete() {
    this.comboSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          if (this.state.filter) {
            this.state.filter = new GridStateCompositeFilterDescriptor(
              GridState.removeFilterByFields(this.state.filter, 'comboName'),
            );
          }

          this.state.filter = this.state.filter?.and({
            field: 'comboName',
            ignoreCase: true,
            operator: 'contains',
            value: searchText,
          });

          this.fetchOverrides(this.state);
        }),
      )
      .subscribe();
  }

  handleFilter(enteredValue: any) {
    this.comboSearch.next(enteredValue);
  }

  cacheChanges() {
    this.overrides.data.forEach((row) => {
      const editableColumns = this.columns.filter((column) => column.editable);
      const isDirty = editableColumns.some((column) =>
        row.dirty.get(column.field),
      );
      if (isDirty) {
        this.cache.set(row.id, row);
      } else {
        this.cache.delete(row.id);
      }

      if (
        (!row.isSecondaryItem &&
          row.dirty.has('newOverridePrice') &&
          row.dirty.get('newOverridePrice')) ||
        (row.dirty.has('newOverrideDeliveryPrice') &&
          row.dirty.get('newOverrideDeliveryPrice'))
      ) {
        // means it is dirty and we need to disable and set the other item to 0
        const pairItems = this.overrides.data.filter(
          (item) =>
            item.comboId === row.comboId &&
            item.componentType !== row.componentType &&
            item.id != row.id &&
            item.isSecondaryItem,
        );

        pairItems.forEach((pairItem) => {
          pairItem.newOverridePrice = 0;
          pairItem.newOverrideDeliveryPrice = 0;
          this.grid.onValueChange(pairItem, 'newOverridePrice', false);
          this.grid.onValueChange(pairItem, 'newOverrideDeliveryPrice', false);
          this.cache.set(pairItem.id, pairItem);
        });
      }

      row.warnings.clear();
      row.errors.clear();
    });
  }

  restoreCachedChanges(): void {
    this.overrides.data.forEach((row) => {
      const cachedRow = this.cache.get(row.id);

      if (cachedRow) {
        const editableColumns = this.columns.filter(
          (column) => column.editable,
        );
        editableColumns.forEach((column) => {
          row[column.field as keyof GridRowOverride] =
            cachedRow[column.field as keyof GridRowOverride];
        });

        row.dirty = cachedRow.dirty;
      }
    });
  }

  saveChanges() {
    this.loaderService.show();
    const changes = Array.from(this.cache.values());

    this.service.saveOverrides(changes).subscribe({
      next: () => {
        this.messageService.success('Upcharge overrides saved successfully');
        this.cache.clear();
        this.setDefaultState();
        this.valueChange.emit();
        this.globalEventsService.updatePendingChanges();
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error('Failed to save overrides');
        const validationError = err?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
        this.loaderService.hide();
      },
    });
  }

  markSecondaryComponents() {
    const groupedByComboId = this.overrides.data.reduce(
      (acc, row) => {
        if (!acc[row.comboId]) {
          acc[row.comboId] = [];
        }
        acc[row.comboId].push(row);
        return acc;
      },
      {} as { [key: number]: GridRowOverride[] },
    );

    Object.keys(groupedByComboId).forEach((comboId) => {
      const rows = groupedByComboId[comboId];
      rows.sort((a: GridRowOverride, b: GridRowOverride) =>
        a.componentType.localeCompare(b.componentType),
      );
      if (rows.length > 1) {
        // Deactivate all but the first row (should be item 1 alphabetically)
        rows.slice(1).forEach((row: GridRowOverride) => {
          if (row.componentType != rows[0].componentType) {
            row.isSecondaryItem = true;
            row.locked = true;
          }
        });
      }
    });
  }

  resetPrice(row : GridRowOverride) {
    row.newOverridePrice = null;
    row.newOverrideDeliveryPrice = null;
    this.grid.onValueChange(row, 'newOverridePrice');
    this.grid.onValueChange(row, 'newOverrideDeliveryPrice');
  }

  onSwitchOverride(
    row: GridRowOverride,
    field: keyof GridRowOverride,
    value: boolean | null,
  ) {
    row.locked = !value;
    if (!value && row.originalValues.get(field) == null) {
      value = null;
    }
    row.newIsOverridePrice = value;

    // if turning off, reset prices to null
    if (!value || value == null) {
      this.resetPrice(row);
    }

    this.grid.onValueChange(row, field);

    // for 2 item combos
    const pairItems = this.overrides.data.filter(
      (item) =>
        item.comboId === row.comboId &&
        item.componentType !== row.componentType &&
        item.id != row.id &&
        item.isSecondaryItem,
    );

    pairItems.forEach((pairItem) => {
      pairItem.newIsOverridePrice = value;

      // if turning off, reset prices to null
      if (!value || value == null) {
        this.resetPrice(pairItem);
      }
      this.grid.onValueChange(pairItem, field);
    });
  }

  onValueChange(dataItem: { [key: string]: any } & GridRowType, field: string) {
    this.grid.onValueChange(dataItem, field);
  }
}
